import { useMemo } from "react";
import t from "@/lang/lang";
import { cn } from "@/shared/lib/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useFieldErrors } from "@/shared/lib/errors/err-validation-failed-context";

export enum Result {
  ZERO = "",
  NONE = "None",
  OK = "OK",
  WARNING = "Warning",
  CRITICAL = "Critical",
}

/* helper for selects */
export const ResultItems = [
  { value: Result.NONE, label: <ResultDot result={Result.NONE} /> },
  { value: Result.OK, label: <ResultDot result={Result.OK} /> },
  { value: Result.WARNING, label: <ResultDot result={Result.WARNING} /> },
  { value: Result.CRITICAL, label: <ResultDot result={Result.CRITICAL} /> },
];

export function ResultDot({
  result,
  showLabel = true,
  hasError,
}: {
  result: Result;
  showLabel?: boolean;
  hasError?: boolean;
}) {
  const { className, label } = useMemo(() => {
    switch (result) {
      case Result.OK:
        return {
          className: "",
          label: t("OK"),
        };
      case Result.WARNING:
        return {
          className: "bg-yellow-300 border-yellow-300",
          label: t("Grenzwertig"),
        };
      case Result.CRITICAL:
        return {
          className: "bg-red-500 border-red-500",
          label: t("Kritisch"),
        };
      case Result.NONE:
        return {
          className: "hidden",
          label: t("Keine Bewertung"),
        };
      case Result.ZERO:
        return {
          className: "bg-background border-background",
          label: "-",
        };
      default:
        return {
          className: "",
          label: "",
        };
    }
  }, [result]);

  if (label === "") {
    return null;
  }

  return (
    <span
      className={cn(
        "flex items-center whitespace-nowrap",
        hasError && "rounded border border-red-500 shadow-md",
      )}
    >
      <span
        className={cn(
          "bg-background inline-block h-4 w-4 rounded-full border",
          showLabel && "mr-2",
          className,
        )}
      />
      {showLabel && label}
    </span>
  );
}

export function ResultSelect({
  result,
  onResultChange,
  className,
  disabled,
  hasError,
  fieldName,
}: {
  result: Result;
  onResultChange: (result: Result) => void;
  className?: string;
  disabled?: boolean;
  /** Marks the select as invalid, combined with the errors of `fieldName`. */
  hasError?: boolean;
  /**
   * The key of the result in the nearest ErrValidationFailedContextProvider. The
   * select is marked invalid while it has errors and resets them on change.
   */
  fieldName?: string;
}) {
  const fieldErrors = useFieldErrors(fieldName);
  const invalid = fieldErrors.invalid || hasError;

  return (
    <Select
      value={result}
      onValueChange={(value: Result) => {
        fieldErrors.reset();
        onResultChange(value);
      }}
      disabled={disabled}
    >
      <SelectTrigger
        aria-invalid={invalid || undefined}
        className={cn(
          "w-full",
          invalid && "border-red-500 shadow-md",
          className,
        )}
      >
        <SelectValue placeholder={t("Bewerten")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          <SelectItem value={Result.NONE}>
            <ResultDot result={Result.NONE} />
          </SelectItem>
          <SelectItem value={Result.OK}>
            <ResultDot result={Result.OK} />
          </SelectItem>
          <SelectItem value={Result.WARNING}>
            <ResultDot result={Result.WARNING} />
          </SelectItem>
          <SelectItem value={Result.CRITICAL}>
            <ResultDot result={Result.CRITICAL} />
          </SelectItem>
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
